// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import PopupContextProvider from "@gcg/App/Components/XPopup/XContext";
import XPopup from "@gcg/App/Components/XPopup/XPopup";
import Button, { ButtonIcon, ButtonPresentationProps, ButtonVariants } from "@gcg/MobiusOverrides/Button";
import { FormFieldPresentationProps } from "@gcg/MobiusOverrides/FormField";
import Select, { SelectComponentProps } from "@gcg/MobiusOverrides/Select";
import ChevronDown from "@gcg/XDL/Icons/ChevronDown";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import buttonDisplayProps from "@insite/mobius/Pagination/buttonDisplayProps";
import Tooltip from "@insite/mobius/Tooltip";
import Typography from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { setCookie } from "@insite/mobius/utilities/cookies";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";

export interface PaginationPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        currentButton?: StyledProp<PaginationProps>;
        ellipsis?: StyledProp<PaginationProps>;
        linkList?: StyledProp<PaginationProps>;
        pagination?: StyledProp<PaginationProps>;
        perPageSelect?: StyledProp<PaginationProps>;
    };
    /** An object containing props to be passed down to the button component inside pagination.
     * @themable */
    buttonProps?: ButtonPresentationProps;
    /** The button variant that should be used for the current button.
     * @themable */
    currentPageButtonVariant?: ButtonVariants;
    /** An object containing props to be passed down to the select component inside pagination
     * @themable */
    selectProps?: FormFieldPresentationProps<SelectComponentProps>;
    /** An object containing icon sources for the navigation button icons.
     * @themable */
    navIconsSrc?: {
        firstPage?: React.ComponentType | string;
        previousPage?: React.ComponentType | string;
        nextPage?: React.ComponentType | string;
        lastPage?: React.ComponentType | string;
    };
}

export type PaginationComponentProps = MobiusStyledComponentProps<
    "div",
    {
        pageSizeCookie?: string;
        /** The index of the current page of results being displayed */
        currentPage: number;
        /** String describing the "results per page" select */
        resultsPerPageLabel?: string;
        /** OnChange function passed to the select component governing the number of results per page. Receives the event as an argument. */
        onChangeResultsPerPage: (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => void;
        /** Function to build href passed to each button in the pagination button/link list. Receives the page index as an argument. */
        createHref?: (page: number) => void;
        /** OnChange function passed to each button in the pagination button/link list. Receives the page index as an argument. */
        onChangePage: (page: number) => void;
        /** Number of results currently being displayed on each page */
        resultsPerPage: number;
        /** Number of total results to be displayed within the pagination component */
        resultsCount: number;
        /** Options to be displayed under the "results per page" select */
        resultsPerPageOptions: number[];
        /** flag to hide perPage dropdown */
        hidePerPage?: boolean;
    }
>;

export type PaginationProps = PaginationPresentationProps & PaginationComponentProps & ThemeProps<BaseTheme>;

const iconButtonStyles = css`
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    width: 24px;
                `,
                null,
                null,
                css`
                    width: 46px;
                `,
                null,
            ],
            "min",
        )};
    justify-content: center;
`;

const PaginationStyle = styled.div<InjectableCss>`
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .xpopup-content {
        div[class^="Popup"] {
            max-height: 315px;
        }
    }

    .popup-content-wrapper {
        padding: 10px;
        max-height: 315px;
        overflow-y: auto;
        width: 100%;

        button {
            width: 100%;
            height: 30px;
            font-weight: normal;
            &.current-page {
                background-color: ${getColor("common.ui3")};
            }
        }
    }
    ${injectCss}
`;

const LinkList = styled.nav<InjectableCss>`
    ${({ theme }) => breakpointMediaQueries(theme, [null, null, null, null, null], "max")};
    ul {
        display: flex;
        justify-content: center;
        align-items: center;
    }
    li {
        &.prev-li,
        &.next-li {
            width: 22px;
            height: 22px;
            display: flex;
            justify-content: center;
            align-items: center;
            button {
                background-color: ${getColor("common.ui3")} !important;
                width: 16px;
                height: 16px;
                border-radius: 50%;
                line-height: 16px;
                margin: 0;
                svg {
                    color: ${getColor("text.main")} !important;
                }
            }
        }
        &.prev-li {
            margin-right: 10px;
        }
        &.next-li {
            margin-left: 10px;
        }
    }
    ${injectCss}
`;

const PerPageSelect = styled.span<InjectableCss>`
    ${({ theme }) => breakpointMediaQueries(theme, [null, null, null, null, null], "max")};
    ${injectCss}
`;

/**
 * Pagination is an interactive component that provides a navigation interface for paged content.
 */
const Pagination: React.FC<PaginationProps> = withTheme(props => {
    const {
        createHref,
        currentPage,
        onChangeResultsPerPage,
        onChangePage,
        resultsPerPage,
        resultsPerPageLabel,
        resultsCount,
        resultsPerPageOptions,
        pageSizeCookie,
        hidePerPage,
        mergeCss,
        ...otherProps
    } = props;

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        if (pageSizeCookie) {
            setCookie(pageSizeCookie, value);
        }

        onChangeResultsPerPage(event, parseInt(value, 10));
    };

    const { spreadProps, applyProp, applyStyledProp } = applyPropBuilder(otherProps, { component: "pagination" });
    const cssOverrides = spreadProps("cssOverrides" as any);
    const buttonProps = spreadProps("buttonProps" as any);
    const currentPageButtonVariant = applyProp("currentPageButtonVariant");
    const translate = otherProps.theme.translate;
    const navIconsSrc = spreadProps("navIconsSrc");
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.pagination?.defaultProps?.mergeCss;

    const finalPageIndex = Math.floor((resultsCount - 1) / resultsPerPage) + 1;

    const numberOfPages = Math.ceil(resultsCount / resultsPerPage);

    const paginationItems = [];
    for (let i = 1; i <= numberOfPages; i++) {
        paginationItems.push(
            <Clickable
                key={`page${i}`}
                onClick={() => onChangePage(i)}
                className={currentPage === i ? "current-page" : undefined}
            >
                <VisuallyHidden>{i}</VisuallyHidden>
                Page {i.toString()}
            </Clickable>,
        );
    }

    const triggerLink = (
        <StyledWrapper>
            <Typography
                size={15}
                lineHeight="18px"
                css={css`
                    margin-right: 8px;
                `}
            >
                Page {currentPage} of {numberOfPages}
            </Typography>
            <Icon src={ChevronDown} color="text" size={10} />
        </StyledWrapper>
    );

    const paginationButtonsWrapper = (
        <li className="xpopup-content">
            <PopupContextProvider>
                <XPopup
                    css={css`
                        z-index: 3;
                        min-width: 110px;
                    `}
                    type="tooltip"
                    trigger={triggerLink}
                    position="bottom center"
                    fixed
                >
                    <StyledWrapper className="popup-content-wrapper">{paginationItems}</StyledWrapper>
                </XPopup>
            </PopupContextProvider>
        </li>
    );

    const forwardDisabled = currentPage === finalPageIndex || finalPageIndex === 1;
    const backDisabled = currentPage === 1 || finalPageIndex === 1;

    const navIcon = (
        pageIndex: number,
        icon: string | React.ComponentType,
        ariaLabel: string,
        disabledFlag: boolean,
        dataTestSelector?: string,
    ) => (
        <Button
            variant="secondary"
            {...buttonDisplayProps(props, {
                page: -1,
                moreCss: iconButtonStyles,
                cssOverrides,
                buttonProps,
                currentPageButtonVariant,
            })}
            aria-label={ariaLabel}
            disabled={disabledFlag}
            onClick={() => onChangePage(pageIndex)}
            {...(dataTestSelector && { "data-test-selector": dataTestSelector })}
            type="button"
        >
            <VisuallyHidden>{ariaLabel}</VisuallyHidden>
            <ButtonIcon size={15} src={icon} />
        </Button>
    );

    return (
        <PaginationStyle
            css={applyStyledProp(["cssOverrides", "pagination"], resolvedMergeCss)}
            {...omitSingle(otherProps, "cssOverrides")}
        >
            {!hidePerPage && (
                <PerPageSelect css={applyStyledProp(["cssOverrides", "perPageSelect"], resolvedMergeCss)}>
                    <Select
                        onChange={changeResultsPerPage}
                        label={resultsPerPageLabel || translate("Page Size")}
                        labelPosition="left"
                        value={resultsPerPage}
                        {...spreadProps("selectProps" as any)}
                        data-test-selector="paginationPerPageSelect"
                    >
                        {resultsPerPageOptions.map((n: string) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </Select>
                </PerPageSelect>
            )}
            <LinkList
                role="navigation"
                aria-label={translate("Pagination navigation")}
                css={applyStyledProp(["cssOverrides", "linkList"], resolvedMergeCss)}
                data-test-selector={`paginationCurrentPage${currentPage}`}
            >
                <ul>
                    <li className="prev-li">
                        {navIcon(
                            currentPage - 1,
                            navIconsSrc.previousPage,
                            translate("Previous page"),
                            backDisabled,
                            "paginationButtonPrevious",
                        )}
                    </li>
                    {paginationButtonsWrapper}
                    <li className="next-li">
                        {navIcon(
                            currentPage + 1,
                            navIconsSrc.nextPage,
                            translate("Next page"),
                            forwardDisabled,
                            "paginationButtonNext",
                        )}
                    </li>
                </ul>
            </LinkList>
        </PaginationStyle>
    );
});

/** @component */
export default withTheme(Pagination) as React.ComponentType<PaginationComponentProps>;

export { Pagination, PaginationStyle };
